// Health Connect / Mi Band bridge (web side).
//
// Kashtu runs unchanged on the web; band sync only exists inside the sideloaded Android shell
// (remote-URL Capacitor — see the kashtu-android repo + docs/android-health-sync-plan.md). The
// Capacitor runtime injects `window.Capacitor` into this remote page, and the custom Kotlin
// `HealthConnect` plugin (Milestone 3) registers under `Capacitor.Plugins.HealthConnect`. Until
// that APK exists every native call here is absent, so `available()` is false and the UI hides
// all of this — the plain web/PWA is completely unaffected.
//
// Division of labour: this module owns the FOREGROUND/online path (read via the plugin → POST
// through the logged-in session). The BACKGROUND path (WorkManager when the WebView is dead)
// posts natively with the scoped device token minted by `connect()` and handed to the plugin to
// store in EncryptedSharedPreferences.

import { api } from "../../api.js";

// The HC getChanges() delta cursor is owned NATIVELY by the plugin (stored in
// EncryptedSharedPreferences and shared with the background WorkManager sync), so JS never tracks
// it — that keeps a single source of truth and avoids re-reading deltas the background worker
// already consumed.

/** The native HealthConnect plugin, or null on plain web / before the plugin ships. */
function plugin() {
  return (typeof window !== "undefined" && window.Capacitor?.Plugins?.HealthConnect) || null;
}

/** True only inside the native Android shell (Capacitor present). */
export function isNative() {
  return typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
}

/** True when the HealthConnect plugin is actually callable — gates every band-sync UI element. */
export function available() {
  return !!plugin();
}

/** Plugin-reported availability + granted permissions, or a uniform "unavailable" shape. */
export async function getStatus() {
  const p = plugin();
  if (!p) return { available: false, granted: false, permissions: [] };
  try {
    return { available: true, ...(await p.getStatus()) };
  } catch (e) {
    return { available: true, granted: false, error: String(e?.message || e) };
  }
}

/**
 * One-time connect: request HC read permissions, then mint the scoped device token and hand it
 * to the plugin to persist for background WorkManager posts. Returns the post-grant status.
 */
export async function connect() {
  const p = plugin();
  if (!p) throw new Error("Health Connect is only available in the Kashtu Android app.");
  await p.requestPermissions();
  const status = await getStatus();
  if (status.granted) {
    try {
      const { token } = await api.healthMintToken();
      if (token && p.setIngestToken) await p.setIngestToken({ token });
    } catch {
      // A failed token mint doesn't block the foreground sync (which uses the session token);
      // background sync simply stays off until the next successful connect/sync.
    }
  }
  return status;
}

/**
 * Foreground delta sync: read workouts the band has written to HC since our last changes cursor,
 * POST them to the idempotent ingest endpoint, advance the cursor. Safe to call on app-open and
 * from a manual "Sync now" button. Returns { synced, result } (result = server counts) or a
 * no-op shape when unavailable.
 */
export async function syncNow() {
  const p = plugin();
  if (!p) return { synced: 0, skipped: "unavailable" };
  // The plugin reads from (and advances) its own native delta cursor; we just forward the result.
  const { workouts = [] } = (await p.readWorkoutsSince()) || {};
  let result = null;
  if (workouts.length) result = await api.ingestHealth(workouts);
  return { synced: workouts.length, result };
}

/** Disconnect on logout/uninstall: revoke server-side + clear the native token and local cursor. */
export async function disconnect() {
  const p = plugin();
  try { await api.healthRevokeToken(); } catch { /* stateless token — best-effort */ }
  // clearIngestToken also wipes the native delta cursor and cancels the background WorkManager sync.
  if (p?.clearIngestToken) { try { await p.clearIngestToken(); } catch { /* ignore */ } }
}

// ── Tier-2 live HR (Milestone 4: workout-only foreground service) ──────────────────────────────
// Starts the native foreground service polling HC heart-rate and forwards each emitted sample to
// the active-session HR endpoint. `onSample(bpm)` lets the Session screen render a live readout.
// Returns a stop function; no-op (returns a noop) when unavailable.
export async function startLiveHr(onSample) {
  const p = plugin();
  if (!p?.startLiveHr) return () => {};
  const handle = await p.addListener?.("hr", (ev) => {
    const bpm = Number(ev?.bpm);
    if (Number.isFinite(bpm)) {
      onSample?.(bpm);
      // Prefer the sample's real Health Connect timestamp over receive-time; fall back if absent.
      // Best-effort persist; a dropped sample is fine (re-aggregated server-side, idempotent).
      const t = ev?.timestamp || new Date().toISOString();
      api.appendLiveHr([{ t, bpm }]).catch(() => {});
    }
  });
  await p.startLiveHr();
  return async () => {
    try { await p.stopLiveHr(); } catch { /* ignore */ }
    try { await handle?.remove?.(); } catch { /* ignore */ }
  };
}
